import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { compileFormula } from '../../../helpers/chart_formula/index.js';

// Stable sentinel, see useDatasourceData.js
const EMPTY_FORMULA_FIELDS = [];

export const FORMULA_ACCESSOR_PREFIX = 'formula_';

export const isFormulaAccessor = (accessor) =>
  typeof accessor === 'string' && accessor.startsWith(FORMULA_ACCESSOR_PREFIX);

/**
 * Formula fields defined on a chart widget (settings.formulaFields).
 *
 * `applyFormulas(row)` returns the formula values for one row, keyed by formula accessor.
 * It must be given rows that are already filtered and pivoted, so formulas see what the chart sees.
 *
 * @param {number} widgetId
 */
export const useFormulaFields = (widgetId) => {
  const formulaFields = useSelector(
    state => state.dashboard.widgets[widgetId]?.settings?.formulaFields ?? EMPTY_FORMULA_FIELDS
  );

  const compiledFields = useMemo(
    () => formulaFields.map(f => ({ ...f, compiled: compileFormula(f.formula) })),
    [formulaFields]
  );

  const applyFormulas = useCallback((row) => {
    const values = {};
    for (const field of compiledFields) {
      values[field.accessor] = field.compiled.evaluate(row);
    }
    return values;
  }, [compiledFields]);

  return { formulaFields, compiledFields, applyFormulas };
};
